// api/simula.ts — G1/G2: Simula mode API client
import { apiJson } from './client';
import type { QuestionDto } from './kb';
import { trackClientEvent } from '../observability/client-events';

const SIMULA_FINALIZE_CORRELATION_HEADER = 'X-Simula-Finalize-Id';
const SIMULA_FINALIZE_CORRELATION_PATTERN = /^sf_[A-Za-z0-9_-]{8,61}$/u;
let fallbackCorrelationSequence = 0;

function createFinalizeCorrelationId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return `sf_${Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')}`;
  }
  fallbackCorrelationSequence = (fallbackCorrelationSequence + 1) % 1_000_000;
  return `sf_${Date.now().toString(36)}_${fallbackCorrelationSequence.toString(36).padStart(4, '0')}`;
}

function resolveFinalizeCorrelationId(correlationId?: string): string {
  if (correlationId === undefined) return createFinalizeCorrelationId();
  if (!SIMULA_FINALIZE_CORRELATION_PATTERN.test(correlationId)) {
    throw new Error('simula_finalize_correlation_invalid');
  }
  return correlationId;
}

function emitFinalizeClientTiming(
  correlationId: string,
  outcome: 'success' | 'failure',
  startedAtMs: number,
): void {
  try {
    console.info(JSON.stringify({
      service: 'simula-client',
      message: 'simula_finalize_timing',
      correlationId,
      outcome,
      totalMs: Math.max(0, Math.round((performance.now() - startedAtMs) * 10) / 10),
    }));
  } catch {
    // Diagnostic telemetry can never change save/retry behavior.
  }
}

export type SimulaMode = 'cieca' | 'guidata' | 'difficile';

export interface SimulaStartResponse {
  simulationId: string;
  questions: QuestionDto[];
}

export interface SimulaFinalizeAnswer {
  questionId: string;
  answer: boolean;
  correctAnswer: boolean;
  responseTimeMs: number;
  /** FP4: whether the student actually answered this question. Unanswered
   *  questions are counted WRONG for the score and EXCLUDED from mastery/SM-2
   *  evidence — fabricating answer=false fed false "correct" + positive mastery
   *  signal whenever the real answer was Falso. */
  answered: boolean;
}

export interface BreakdownEntry {
  argomentoName: string;
  correct: number;
  total: number;
}

export type SimulaDurability =
  | 'saved'
  | 'already_saved'
  | 'result_saved_mastery_deferred';

export interface SimulaFinalizeResponse {
  score: number;
  passed: boolean;
  breakdown: BreakdownEntry[];
  probability: number | null;
  durability: SimulaDurability;
  mastery_status?: 'committed' | 'legacy_unverified';
}

export const simulaApi = {
  async start(
    mode: SimulaMode,
    excludeQuestionIds?: string[],
  ): Promise<SimulaStartResponse> {
    const body: { mode: SimulaMode; excludeQuestionIds?: string[] } = { mode };
    if (excludeQuestionIds && excludeQuestionIds.length > 0) {
      // The start schema caps this at 500; keep the payload bounded.
      body.excludeQuestionIds = excludeQuestionIds.slice(0, 500);
    }
    return apiJson<SimulaStartResponse>('/api/simula/start', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async answer(
    simulationId: string,
    data: { questionId: string; answer: boolean; responseTimeMs: number },
  ): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(`/api/simula/${simulationId}/answer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async finalize(
    simulationId: string,
    mode: SimulaMode,
    answers: SimulaFinalizeAnswer[],
    requestedCorrelationId?: string,
  ): Promise<SimulaFinalizeResponse> {
    const correlationId = resolveFinalizeCorrelationId(requestedCorrelationId);
    const startedAtMs = performance.now();
    let outcome: 'success' | 'failure' = 'failure';
    try {
      const result = await apiJson<SimulaFinalizeResponse>(`/api/simula/${simulationId}/finalize`, {
        method: 'POST',
        headers: { [SIMULA_FINALIZE_CORRELATION_HEADER]: correlationId },
        body: JSON.stringify({ mode, answers }),
      });
      outcome = 'success';
      void trackClientEvent({
        eventKind: 'save', surface: 'simula', outcome: 'success', code: 'exam_result_saved',
      });
      return result;
    } catch (error) {
      void trackClientEvent({
        eventKind: 'save', surface: 'simula', outcome: 'failure', code: 'exam_result_save_failed',
      });
      throw error;
    } finally {
      emitFinalizeClientTiming(correlationId, outcome, startedAtMs);
    }
  },
};
